import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const BrandStory: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    return (
        <section className="py-24 bg-neutral-50 relative overflow-hidden">
            <div className="container mx-auto px-4">
                <div className="flex flex-col lg:flex-row items-center gap-16">
                    {/* Image Side */}
                    <div className={`w-full lg:w-1/2 ${isRTL ? 'lg:order-2' : 'lg:order-1'}`}>
                        <div className="relative">
                            <motion.div
                                className="relative z-10 rounded-3xl overflow-hidden shadow-2xl"
                                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                            >
                                <img
                                    src="https://images.unsplash.com/photo-1616401784845-180882ba9ba8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                                    alt="Our Story"
                                    className="w-full h-auto object-cover"
                                />
                            </motion.div>

                            {/* Decorative elements */}
                            <motion.div
                                className="absolute -top-10 -left-10 w-40 h-40 bg-primary/10 rounded-full blur-2xl -z-10"
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 5, repeat: Infinity }}
                            ></motion.div>
                            <motion.div
                                className="absolute -bottom-10 -right-10 w-60 h-60 bg-accent/10 rounded-full blur-3xl -z-10"
                                animate={{ scale: [1, 1.1, 1] }}
                                transition={{ duration: 7, repeat: Infinity }}
                            ></motion.div>

                            {/* Experience Badge */}
                            <motion.div
                                className={`absolute bottom-10 ${isRTL ? '-left-6' : '-right-6'} bg-white p-6 rounded-2xl shadow-xl z-20 max-w-xs`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.4, duration: 0.6 }}
                            >
                                <p className="text-4xl font-bold text-primary mb-1">10+</p>
                                <p className="text-gray-600 font-medium">{t('brandStory.years', 'Years of Excellence in Natural Products')}</p>
                            </motion.div>
                        </div>
                    </div>

                    {/* Content Side */}
                    <div className={`w-full lg:w-1/2 ${isRTL ? 'lg:order-1' : 'lg:order-2'}`}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className="text-primary font-semibold tracking-wider uppercase text-sm mb-2 block">
                                {t('brandStory.subtitle', 'Our Promise')}
                            </span>
                            <h2 className="text-3xl md:text-5xl font-bold text-dark mb-6 leading-tight">
                                {t('brandStory.title', 'Bringing Nature\'s Best to Your Doorstep')}
                            </h2>
                            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                {t('brandStory.p1', 'At Farhan, we believe in the power of nature to heal and rejuvenate. Our journey began with a simple mission: to provide authentic, high-quality natural products that you can trust.')}
                            </p>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                {t('brandStory.p2', 'Every product in our collection is carefully selected and rigorously tested to ensure it meets our high standards of purity and effectiveness. We are committed to sustainability and ethical sourcing.')}
                            </p>

                            <Link
                                to="/about"
                                className="inline-flex items-center text-primary font-bold text-lg hover:text-primary-dark transition-colors group"
                            >
                                {t('brandStory.readMore', 'Read Our Full Story')}
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 ${isRTL ? 'mr-2 group-hover:-translate-x-1' : 'ml-2'}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} />
                                </svg>
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BrandStory;
